/**
 * Social Media Configuration
 * 
 * Centralized configuration for all social media platforms,
 * credentials, and service initialization.
 */

import { SocialPlatform, PlatformConfig, PlatformCredentials, SocialMediaService } from '../types/social';
import { FacebookService, MockFacebookService } from '../api/social/facebook';
import { TwitterService, MockTwitterService } from '../api/social/twitter';

/**
 * Platform configurations with branding and API settings
 */
export const PLATFORM_CONFIGS: Record<SocialPlatform, PlatformConfig> = {
  facebook: {
    name: 'facebook',
    displayName: 'Facebook',
    icon: '📘',
    color: '#1877F2',
    apiEndpoint: 'https://graph.facebook.com/v18.0',
    rateLimit: {
      requests: 200,
      window: 60 * 60 * 1000 // 1 hour
    }
  },
  instagram: {
    name: 'instagram',
    displayName: 'Instagram',
    icon: '📷',
    color: '#E4405F',
    apiEndpoint: 'https://graph.instagram.com',
    rateLimit: {
      requests: 200,
      window: 60 * 60 * 1000 // 1 hour
    }
  },
  twitter: {
    name: 'twitter',
    displayName: 'X (Twitter)',
    icon: '🐦',
    color: '#1DA1F2',
    apiEndpoint: 'https://api.twitter.com/2',
    rateLimit: {
      requests: 75,
      window: 15 * 60 * 1000 // 15 minutes
    }
  },
  linkedin: {
    name: 'linkedin',
    displayName: 'LinkedIn',
    icon: '💼',
    color: '#0A66C2',
    apiEndpoint: 'https://api.linkedin.com/v2',
    rateLimit: {
      requests: 500,
      window: 24 * 60 * 60 * 1000 // 24 hours
    }
  },
  youtube: {
    name: 'youtube',
    displayName: 'YouTube',
    icon: '📺',
    color: '#FF0000',
    apiEndpoint: 'https://www.googleapis.com/youtube/v3',
    rateLimit: {
      requests: 10000,
      window: 24 * 60 * 60 * 1000 // 24 hours
    }
  },
  tiktok: {
    name: 'tiktok',
    displayName: 'TikTok',
    icon: '🎵',
    color: '#000000',
    apiEndpoint: 'https://open-api.tiktok.com',
    rateLimit: {
      requests: 100,
      window: 60 * 60 * 1000 // 1 hour
    }
  }
};

/**
 * Default platform credentials structure
 * These should be configured via environment variables
 */
export const DEFAULT_CREDENTIALS: Record<SocialPlatform, PlatformCredentials> = {
  facebook: {
    platform: 'facebook',
    credentials: {
      accessToken: import.meta.env.VITE_FACEBOOK_ACCESS_TOKEN,
      apiKey: import.meta.env.VITE_FACEBOOK_APP_ID,
      apiSecret: import.meta.env.VITE_FACEBOOK_APP_SECRET,
      userId: import.meta.env.VITE_FACEBOOK_PAGE_ID
    },
    config: {
      maxPosts: 25,
      includeReplies: false,
      includeRetweets: true,
      mediaOnly: false
    }
  },
  instagram: {
    platform: 'instagram',
    credentials: {
      accessToken: import.meta.env.VITE_INSTAGRAM_ACCESS_TOKEN,
      userId: import.meta.env.VITE_INSTAGRAM_BUSINESS_ACCOUNT_ID
    },
    config: {
      maxPosts: 25,
      mediaOnly: true
    }
  },
  twitter: {
    platform: 'twitter',
    credentials: {
      accessToken: import.meta.env.VITE_TWITTER_BEARER_TOKEN,
      username: import.meta.env.VITE_TWITTER_USERNAME
    },
    config: {
      maxPosts: 10,
      includeReplies: false,
      includeRetweets: false,
      mediaOnly: false
    }
  },
  linkedin: {
    platform: 'linkedin',
    credentials: {
      accessToken: import.meta.env.VITE_LINKEDIN_ACCESS_TOKEN,
      userId: import.meta.env.VITE_LINKEDIN_USER_ID
    },
    config: {
      maxPosts: 20
    }
  },
  youtube: {
    platform: 'youtube',
    credentials: {
      apiKey: import.meta.env.VITE_YOUTUBE_API_KEY,
      userId: import.meta.env.VITE_YOUTUBE_CHANNEL_ID
    },
    config: {
      maxPosts: 15
    }
  },
  tiktok: {
    platform: 'tiktok',
    credentials: {
      accessToken: import.meta.env.VITE_TIKTOK_ACCESS_TOKEN,
      userId: import.meta.env.VITE_TIKTOK_USER_ID
    },
    config: {
      maxPosts: 20,
      mediaOnly: true
    }
  }
};

/**
 * Social media service factory
 */
export class SocialMediaServiceFactory {
  private static services: Map<SocialPlatform, SocialMediaService> = new Map();

  /**
   * Get service instance for a platform
   */
  static getService(platform: SocialPlatform, useMock = false): SocialMediaService {
    const cacheKey = `${platform}_${useMock ? 'mock' : 'real'}`;
    
    if (this.services.has(platform)) {
      return this.services.get(platform)!;
    }

    let service: SocialMediaService;

    if (useMock || !this.hasValidCredentials(platform)) {
      service = this.createMockService(platform);
    } else {
      service = this.createRealService(platform);
    }

    this.services.set(platform, service);
    return service;
  }

  /**
   * Check if platform has valid credentials
   */
  private static hasValidCredentials(platform: SocialPlatform): boolean {
    const creds = DEFAULT_CREDENTIALS[platform];
    
    switch (platform) {
      case 'facebook':
      case 'instagram':
        return !!(creds.credentials.accessToken);
      case 'twitter':
        return !!(creds.credentials.accessToken);
      case 'linkedin':
        return !!(creds.credentials.accessToken && creds.credentials.userId);
      case 'youtube':
        return !!(creds.credentials.apiKey && creds.credentials.userId);
      case 'tiktok':
        return !!(creds.credentials.accessToken && creds.credentials.userId);
      default:
        return false;
    }
  }

  /**
   * Create real service instance
   */
  private static createRealService(platform: SocialPlatform): SocialMediaService {
    const creds = DEFAULT_CREDENTIALS[platform];

    switch (platform) {
      case 'facebook':
      case 'instagram':
        return new FacebookService({
          accessToken: creds.credentials.accessToken!,
          pageId: creds.credentials.userId,
          instagramBusinessAccountId: platform === 'instagram' ? creds.credentials.userId : undefined
        });

      case 'twitter':
        return new TwitterService({
          bearerToken: creds.credentials.accessToken!
        });

      default:
        throw new Error(`Real service not implemented for platform: ${platform}`);
    }
  }

  /**
   * Create mock service instance
   */
  private static createMockService(platform: SocialPlatform): SocialMediaService {
    switch (platform) {
      case 'facebook':
      case 'instagram':
        return new MockFacebookService();
      case 'twitter':
        return new MockTwitterService();
      default:
        return new MockTwitterService(); // Use Twitter mock as fallback
    }
  }

  /**
   * Clear service cache
   */
  static clearCache(): void {
    this.services.clear();
  }

  /**
   * Get all available platforms
   */
  static getAvailablePlatforms(): SocialPlatform[] {
    return Object.keys(PLATFORM_CONFIGS) as SocialPlatform[];
  }

  /**
   * Get platform configuration
   */
  static getPlatformConfig(platform: SocialPlatform): PlatformConfig {
    return PLATFORM_CONFIGS[platform];
  }

  /**
   * Validate platform credentials
   */
  static async validatePlatformCredentials(platform: SocialPlatform): Promise<boolean> {
    try {
      const service = this.getService(platform, false);
      return await service.validateCredentials();
    } catch (error) {
      console.error(`Failed to validate ${platform} credentials:`, error);
      return false;
    }
  }
}

/**
 * Social media configuration defaults
 */
export const SOCIAL_CONFIG = {
  defaultPlatform: 'facebook' as SocialPlatform,
  defaultLimit: 10,
  cacheDuration: 5 * 60 * 1000, // 5 minutes
  refreshInterval: 10 * 60 * 1000, // 10 minutes
  enableAnalytics: true,
  useMockData: !import.meta.env.PROD, // Use mock data in development
  
  // Platform-specific settings
  platformSettings: {
    facebook: {
      fieldsToFetch: [
        'id', 'message', 'story', 'full_picture', 'picture', 
        'created_time', 'updated_time', 'permalink_url', 'type',
        'from{id,name,picture}', 'likes.summary(true)', 
        'comments.summary(true)', 'shares'
      ]
    },
    instagram: {
      fieldsToFetch: [
        'id', 'caption', 'media_type', 'media_url', 
        'thumbnail_url', 'timestamp', 'permalink'
      ]
    },
    twitter: {
      fieldsToFetch: [
        'created_at', 'author_id', 'conversation_id', 
        'public_metrics', 'entities', 'attachments'
      ]
    }
  }
};

/**
 * Environment validation
 */
export function validateSocialEnvironment(): {
  valid: boolean;
  missing: string[];
  configured: SocialPlatform[];
} {
  const missing: string[] = [];
  const configured: SocialPlatform[] = [];

  // Check Facebook
  if (import.meta.env.VITE_FACEBOOK_ACCESS_TOKEN) {
    configured.push('facebook');
  } else {
    missing.push('VITE_FACEBOOK_ACCESS_TOKEN');
  }

  // Check Instagram
  if (import.meta.env.VITE_INSTAGRAM_ACCESS_TOKEN && import.meta.env.VITE_INSTAGRAM_BUSINESS_ACCOUNT_ID) {
    configured.push('instagram');
  } else {
    missing.push('VITE_INSTAGRAM_ACCESS_TOKEN', 'VITE_INSTAGRAM_BUSINESS_ACCOUNT_ID');
  }

  // Check Twitter
  if (import.meta.env.VITE_TWITTER_BEARER_TOKEN && import.meta.env.VITE_TWITTER_USERNAME) {
    configured.push('twitter');
  } else {
    missing.push('VITE_TWITTER_BEARER_TOKEN', 'VITE_TWITTER_USERNAME');
  }

  return {
    valid: configured.length > 0,
    missing: [...new Set(missing)], // Remove duplicates
    configured
  };
}